<template>
  <header class="header-main">
    <b-navbar toggleable="xl" class="justify-content-center">
      <b-navbar-toggle target="nav-collapse" @click="toggleMobileMenu">
        <!-- #default="{ expanded }" -->
        <template #default="{ expanded }">
          <span class="icon-hamburger" :class="{ open: expanded }" aria-label="Toggle menu"
            aria-expanded="expanded.toString()">
            <span></span>
            <span></span>
            <span></span>
        </span>
        </template>
      </b-navbar-toggle>

      <!-- Navbar brand logo -->
      <b-navbar-brand :href="makePageLink()">
        <img class="black-logo" src="/assets/images/logo/logo-black.png" alt="MyWhoosh Logo" />
        <img class="white-logo d-none" src="/assets/images/logo/logo-white.png" alt="MyWhoosh Logo" />
      </b-navbar-brand>

      <b-collapse id="nav-collapse" is-nav @show="isMenuOpen = true" @hide="isMenuOpen = false">
        <!-- Left aligned nav items -->
        <b-navbar-nav class="w-100">
          <b-nav-item :href="makePageLink('events')">Events</b-nav-item>
          <b-nav-item :href="makePageLink('routes')">Routes</b-nav-item>
          <b-nav-item :href="makePageLink('blog')">Blog</b-nav-item>
          <b-nav-item :href="makePageLink('results')">Results</b-nav-item>
          <b-nav-item :href="makePageLink('docs')">Support</b-nav-item>
          <b-nav-item target="_blank" :href="makePageLink('uci')">2024 UCI CEWC</b-nav-item>
          <b-nav-item target="_blank" :href="makePageLink('store')">Shop</b-nav-item>

          <li class="d-none d-xl-block ml-auto"></li>

          <b-nav-item :href="makePageLink('podcast')">Podcast</b-nav-item>
          <b-nav-item :href="makePageLink('getting-started-mywhoosh-cycling-app')">Download App</b-nav-item>
          <b-nav-item :href="makePageLink('how-it-works')">Get Started</b-nav-item>
          <b-nav-item :href="makePageLink('about')">About Us</b-nav-item>

          <template v-if="loggedIn">
            <b-nav-item class="d-xl-none" :to="{ name: 'new_activities', hash: '#profile', }">View Profile</b-nav-item>
            <b-nav-item class="d-xl-none" :to="{ name: 'new_activities', hash: '#activities', }">Activities</b-nav-item>
            <b-nav-item class="d-xl-none" :to="{ name: 'userWallet' }">Wallet</b-nav-item>
            <b-nav-item class="d-xl-none" href="javascript:void(0)" @click="logout">Sign Out</b-nav-item>

            <b-nav-item-dropdown class="d-none d-xl-block" right>
              <!-- Using 'button-content' slot -->
              <template #button-content>
                <em>{{ user.firstname.substring(0, 1) }}</em>
              </template>
              <b-dropdown-item :to="{ name: 'new_activities', hash: '#profile', }">View Profile</b-dropdown-item>
              <b-dropdown-item :to="{ name: 'new_activities', hash: '#activities', }">Activities</b-dropdown-item>
              <b-dropdown-item :to="{ name: 'userWallet' }">Wallet</b-dropdown-item>
              <b-dropdown-item href="javascript:void(0)" @click="logout">Sign Out</b-dropdown-item>
            </b-nav-item-dropdown>
          </template>

          <template v-else>
            <b-nav-item :to="{ name: 'login' }">Login</b-nav-item>
            <b-nav-item :to="{ name: 'register' }">Signup</b-nav-item>
          </template>
        </b-navbar-nav>
      </b-collapse>
    </b-navbar>
  </header>
</template>

<script>
import { mapGetters } from "vuex";
import { commonMixin } from "@/mixins/commonMixin";

export default {
  name: "HeaderComponent",
  mixins: [commonMixin],
  head: {
    script: [
      {
        src: "/assets/js/activities/bootstrap.bundle.min.js",
        type: "text/javascript",
      },
    ],
  },
  data() {
    return {
      wpURL: process.env.VUE_APP_WEB_URL,
      storeBaseUrl: process.env.VUE_APP_WHOOSH_STORE_BASE_URL || "",
      isMenuOpen: false, // New data property to track mobile menu state
    };
  },

  watch: {
    isMenuOpen(newValue) {
      if (newValue) {
        document.body.classList.add('no-scroll');
      } else {
        document.body.classList.remove('no-scroll');
      }
    }
  },

  computed: {
    ...mapGetters("auth", ["user", "loggedIn"]),
    ...mapGetters("user", ["avatar", "profile"]),

    getUserAvatar() {
      const def = this.avatar.split("/");
      if (def[def.length - 1] === "avatar.png") {
        return this.assetsUrl() + "/images/" + this.avatar;
      }
      return this.makeImage(this.avatar, 350, 350, true);
    },

    getUserName() {
      if (!this.profile) return;
      return this.profile.firstname + " " + this.profile.lastname;
    },

    getUserNameForMenu() {
      if (this.user) {
        return this.user.firstname + " " + this.user.lastname;
      }

      return null;
    },

    makeQueryString() {
      if (!this.user) return `?login=false`;
      return `?login=${this.loggedIn}&profile=${this.getUserNameForMenu}`;
    },
  },

  mounted() {
    // check if player is logged in
    this.$store.dispatch("auth/loggedIn");
  },

  methods: {
    toggleMobileMenu() {
      this.isMenuOpen = !this.isMenuOpen;
    },

    makePageLink(slug = null) {
      // Define supported hostnames and their corresponding prefixes and domains
      const hostnamesWithPrefixes = [
        {
          hostname: "service20-dev03.my-whoosh.com",
          prefix: "dev03",
          domain: ".my-whoosh.com",
        },
        {
          hostname: "service20-dev02.my-whoosh.com",
          prefix: "dev02",
          domain: ".my-whoosh.com",
        },
        {
          hostname: "service20-dev01.my-whoosh.com",
          prefix: "dev01",
          domain: ".my-whoosh.com",
        },
        {
          hostname: "service20-stage.my-whoosh.com",
          prefix: "stage",
          domain: ".my-whoosh.com",
        },
        {
          hostname: "service20-qa.my-whoosh.com",
          prefix: "qa",
          domain: ".my-whoosh.com",
        },
        {
          hostname: "service20-int.my-whoosh.com",
          prefix: "int",
          domain: ".my-whoosh.com",
        },
        {
          hostname: "event.mywhoosh.com",
          prefix: "",
          domain: ".mywhoosh.com",
        }, // Production has no prefix
      ];

      const currentHostname = window.location.hostname;

      // Use find to get the matching hostname object
      const envPrefixObj = hostnamesWithPrefixes.find(
        (item) => currentHostname === item.hostname
      );
      const isDevOrStage = !!envPrefixObj; // Check if a matching hostname was found
      const envPrefix = envPrefixObj
        ? envPrefixObj.prefix
          ? `-${envPrefixObj.prefix}`
          : ""
        : "";
      const domain = envPrefixObj ? envPrefixObj.domain : ""; // Get the domain

      // Define URLs based on slug and environment
      if (slug === "uci" && isDevOrStage) {
        return `https://uci${envPrefix}${domain}`;
      } else if (slug === "store" && isDevOrStage) {
        return `https://store${envPrefix}${domain}`;
      } else if (slug) {
        return `${this.wpWebUrl()}/${slug}/${this.makeQueryString}`;
      }

      return `${this.wpWebUrl()}/${this.makeQueryString}`;
    },

    logout() {
      this.$store.dispatch("auth/logout");
      this.$router.push({ name: "events" });
    },
  },
};
</script>

<style scoped>
body.no-scroll {
  overflow: hidden;
}

.icon-hamburger {
  width: 30px;
  height: 22px;
  position: relative;
  display: inline-block;
  cursor: pointer;
  border: none;
  background: transparent;
  padding: 0;
}

.icon-hamburger span {
  display: block;
  position: absolute;
  height: 3px;
  width: 100%;
  background: #282828;
  border-radius: 2px;
  opacity: 1;
  left: 0;
  transition: all 0.3s ease;
}

.icon-hamburger span:nth-child(1) {
  top: 0;
}

.icon-hamburger span:nth-child(2) {
  top: 9px;
}

.icon-hamburger span:nth-child(3) {
  top: 18px;
}

.icon-hamburger.open span:nth-child(1) {
  top: 9px;
  transform: rotate(45deg);
}

.icon-hamburger.open span:nth-child(2) {
  opacity: 0;
}

.icon-hamburger.open span:nth-child(3) {
  top: 9px;
  transform: rotate(-45deg);
}

.header-main {
  background-color: #FFFFFF;
  font-family: "Inter", sans-serif;
  height: 96px;
  left: 0;
  padding: 1rem 0;
  position: absolute;
  right: 0;
  top: 0;
  z-index: 1000;
}

.navbar {
  font-family: "Inter", sans-serif;
  max-width: 1750px;
}

.navbar-light {
  background-color: #FFFFFF;
  margin: 0 auto;
  padding: 0 4%;
  width: 100%;
}

.navbar-light .navbar-nav .nav-link {
  color: #121212;
}

.navbar-nav .nav-link,
.navbar-expand-xl .navbar-nav .nav-link {
  font-size: 12px;
  font-weight: 400;
  letter-spacing: 0.15em;
  line-height: 56px;
  margin-right: 25px;
  padding: 0;
  text-transform: uppercase;
  white-space: nowrap;
}

.navbar-nav .nav-link:hover,
.navbar-nav .nav-link:focus {
  color: #121212;
  text-decoration: underline;
}

.navbar-nav .dropdown-toggle {
  line-height: 56px;
  line-height: normal;
  margin: 0;
  padding: 0;
}

.navbar-nav .dropdown-toggle::after {
  content: none;
}

.navbar-nav .dropdown-toggle em {
  align-items: center;
  background-color: #FCB913;
  border-radius: 50%;
  display: inline-flex;
  font-size: 2rem;
  font-weight: 400;
  height: 56px;
  justify-content: center;
  text-align: center;
  text-transform: uppercase;
  width: 56px;
}

.navbar-nav .dropdown-menu {
  border-radius: 0;
  border: 0;
  margin: 0;
  padding: 0;
  width: 230px;
  box-shadow: 0 8px 12px 4px rgba(0, 0, 0, 0.15);
}

.navbar-nav .dropdown-menu .dropdown-item {
  color: #282828;
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.5rem;
  padding: 1rem 1.5rem;
  text-transform: uppercase;
  transition: all ease-in-out 0.2s;
}

.navbar-nav .dropdown-menu .dropdown-item:hover,
.navbar-nav .dropdown-menu .dropdown-item:focus {
  background-color: #FCB913;
}

.navbar-brand {
  left: 50%;
  margin: 0;
  padding: 0;
  position: absolute;
  transform: translateX(-50%);
  z-index: 1000;
}

.navbar-brand img {
  height: auto;
  max-height: 60px;
  width: 135px;
}

@media (max-width: 1200px) {
  .header-main {
    border-bottom: 1px solid #f2f2f2;
    position: fixed;
    z-index: 9999;
  }

  .navbar-light {
    padding: 0 1.5rem;
  }

  .navbar-brand {
    position: relative;
    left: auto;
    transform: none;
  }

  .navbar-nav .nav-link,
  .navbar-expand-xl .navbar-nav .nav-link {
    margin: 0;
    line-height: 32px;
    padding: 0.5rem 0;
  }

  /* Navbar Slide Animation */
  .navbar .navbar-toggler {
    align-items: center;
    border-radius: 0;
    border: 0;
    color: #282828;
    display: inline-flex;
    font-size: 1.75rem;
    height: 32px;
    justify-content: center;
    left: 1.25rem;
    margin: 0;
    outline: none;
    padding: 0;
    position: absolute;
    top: 1rem;
    width: 32px;
    z-index: 1000;
  }

  svg path {
    transition: d 0.2s ease-in-out;
  }

  .navbar-collapse {
    background-color: #FFFFFF;
    position: fixed;
    padding: 0 1.5rem 2rem;
    top: 96px;
    left: 0;
    width: 100%;
    height: calc(100vh - 96px);
    z-index: 99;
    overflow-y: auto;
    transform: translateX(-100%);
  }

  .navbar-collapse.collapsing {
    height: 100vh !important;
    overflow: hidden;
    transition: none !important;
  }

  .navbar-collapse.show {
    animation: slideToRight 0.3s forwards;
  }

  .navbar-collapse.collapsing:not(.show) {
    animation: slideToLeft 0.3s forwards;
  }

  .navbar-collapse:not(.show):not(.collapsing) {
    transform: translateX(-100%);
  }
}

@media (max-width: 767px) {
  body {
    padding-top: 0;
  }
}

/* Keyframe animations for slide */
@keyframes slideToRight {
  from {
    transform: translateX(-100%);
  }

  to {
    transform: translateX(0);
  }
}

@keyframes slideToLeft {
  from {
    transform: translateX(0);
  }

  to {
    transform: translateX(-100%);
  }
}

</style>
